import { useState, useMemo, useCallback } from 'react'
import {
  Stack,
  Group,
  Switch,
  MultiSelect,
  TagsInput,
  Button,
  Text,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import type { AccessScopes } from '@paperair/shared-types'
import { isAllAccess, SCOPE_ALL_ACCESS } from '@paperair/shared-types'
import type { Client } from '../api/clientsApi'
import { getErrorMessage } from '../api/usersApi'

interface ScopeEditorProps {
  scopes: AccessScopes
  clients: Client[]
  onSave: (scopes: AccessScopes) => Promise<void>
  disabled?: boolean
}

interface ScopeFieldState {
  allAccess: boolean
  values: string[]
}

type ScopeState = Record<keyof AccessScopes, ScopeFieldState>

function initFieldState(value: string[] | null): ScopeFieldState {
  return {
    allAccess: isAllAccess(value),
    values: value && !isAllAccess(value) ? value : [],
  }
}

function scopeStateToAccessScopes(state: ScopeState): AccessScopes {
  const toArray = (field: ScopeFieldState): string[] =>
    field.allAccess ? [SCOPE_ALL_ACCESS] : field.values

  return {
    studioFilter: toArray(state.studioFilter),
    formatPartnerFilter: toArray(state.formatPartnerFilter),
    countryFilter: toArray(state.countryFilter),
    exhibitorAssociations: toArray(state.exhibitorAssociations),
  }
}

function buildClientOptions(clients: Client[], type: 'studio' | 'formatPartner') {
  return clients
    .filter((c) => c.type === type && c.isActive)
    .map((c) => ({ value: c._id, label: c.name }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function ScopeEditor({ scopes, clients, onSave, disabled }: ScopeEditorProps) {
  const [state, setState] = useState<ScopeState>(() => ({
    studioFilter: initFieldState(scopes.studioFilter),
    formatPartnerFilter: initFieldState(scopes.formatPartnerFilter),
    countryFilter: initFieldState(scopes.countryFilter),
    exhibitorAssociations: initFieldState(scopes.exhibitorAssociations),
  }))
  const [saving, setSaving] = useState(false)

  const studioOptions = useMemo(() => buildClientOptions(clients, 'studio'), [clients])
  const formatPartnerOptions = useMemo(() => buildClientOptions(clients, 'formatPartner'), [clients])

  // Build id→name map for resolving client IDs in current values
  const clientNameMap = useMemo(() => {
    const map = new Map<string, string>()
    clients.forEach((c) => map.set(c._id, c.name))
    return map
  }, [clients])

  // Check if state differs from original scopes
  const hasChanges = useMemo(() => {
    const current = scopeStateToAccessScopes(state)
    const fields: (keyof AccessScopes)[] = ['studioFilter', 'formatPartnerFilter', 'countryFilter', 'exhibitorAssociations']
    return fields.some((field) => {
      const orig = scopes[field]
      const cur = current[field]
      const origNorm = isAllAccess(orig) ? ['*'] : (orig ?? [])
      return JSON.stringify(origNorm) !== JSON.stringify(cur)
    })
  }, [state, scopes])

  const updateField = useCallback(
    (field: keyof AccessScopes, update: Partial<ScopeFieldState>) => {
      setState((prev) => ({
        ...prev,
        [field]: { ...prev[field], ...update },
      }))
    },
    []
  )

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      await onSave(scopeStateToAccessScopes(state))
      notifications.show({
        title: 'Scopes Updated',
        message: 'Access scopes saved successfully',
        color: 'green',
      })
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }, [state, onSave])

  // For MultiSelect: ensure current values that might be IDs not in options
  // still show with a resolved label
  const studioData = useMemo(() => {
    const optionIds = new Set(studioOptions.map((o) => o.value))
    const extra = state.studioFilter.values
      .filter((id) => !optionIds.has(id))
      .map((id) => ({ value: id, label: clientNameMap.get(id) || id }))
    return [...studioOptions, ...extra]
  }, [studioOptions, state.studioFilter.values, clientNameMap])

  const formatPartnerData = useMemo(() => {
    const optionIds = new Set(formatPartnerOptions.map((o) => o.value))
    const extra = state.formatPartnerFilter.values
      .filter((id) => !optionIds.has(id))
      .map((id) => ({ value: id, label: clientNameMap.get(id) || id }))
    return [...formatPartnerOptions, ...extra]
  }, [formatPartnerOptions, state.formatPartnerFilter.values, clientNameMap])

  return (
    <Stack gap="sm">
      {/* Studio Filter */}
      <div>
        <Group justify="space-between" mb={4}>
          <Text size="sm" c="dimmed">Studio Filter</Text>
          <Switch
            label="All"
            size="xs"
            checked={state.studioFilter.allAccess}
            onChange={(e) => updateField('studioFilter', { allAccess: e.currentTarget.checked })}
            disabled={disabled || saving}
          />
        </Group>
        {!state.studioFilter.allAccess && (
          <MultiSelect
            data={studioData}
            value={state.studioFilter.values}
            onChange={(values) => updateField('studioFilter', { values })}
            placeholder="Search studios..."
            searchable
            clearable
            disabled={disabled || saving}
            size="sm"
          />
        )}
      </div>

      {/* Format Partner Filter */}
      <div>
        <Group justify="space-between" mb={4}>
          <Text size="sm" c="dimmed">Format Partner Filter</Text>
          <Switch
            label="All"
            size="xs"
            checked={state.formatPartnerFilter.allAccess}
            onChange={(e) => updateField('formatPartnerFilter', { allAccess: e.currentTarget.checked })}
            disabled={disabled || saving}
          />
        </Group>
        {!state.formatPartnerFilter.allAccess && (
          <MultiSelect
            data={formatPartnerData}
            value={state.formatPartnerFilter.values}
            onChange={(values) => updateField('formatPartnerFilter', { values })}
            placeholder="Search format partners..."
            searchable
            clearable
            disabled={disabled || saving}
            size="sm"
          />
        )}
      </div>

      {/* Country Filter */}
      <div>
        <Group justify="space-between" mb={4}>
          <Text size="sm" c="dimmed">Country Filter</Text>
          <Switch
            label="All"
            size="xs"
            checked={state.countryFilter.allAccess}
            onChange={(e) => updateField('countryFilter', { allAccess: e.currentTarget.checked })}
            disabled={disabled || saving}
          />
        </Group>
        {!state.countryFilter.allAccess && (
          <TagsInput
            value={state.countryFilter.values}
            onChange={(values) => updateField('countryFilter', { values })}
            placeholder="Type a country and press Enter"
            disabled={disabled || saving}
            size="sm"
          />
        )}
      </div>

      {/* Exhibitor Associations */}
      <div>
        <Group justify="space-between" mb={4}>
          <Text size="sm" c="dimmed">Exhibitor Associations</Text>
          <Switch
            label="All"
            size="xs"
            checked={state.exhibitorAssociations.allAccess}
            onChange={(e) => updateField('exhibitorAssociations', { allAccess: e.currentTarget.checked })}
            disabled={disabled || saving}
          />
        </Group>
        {!state.exhibitorAssociations.allAccess && (
          <TagsInput
            value={state.exhibitorAssociations.values}
            onChange={(values) => updateField('exhibitorAssociations', { values })}
            placeholder="Type an exhibitor ID and press Enter"
            disabled={disabled || saving}
            size="sm"
          />
        )}
      </div>

      {hasChanges && (
        <Group justify="flex-end">
          <Button
            size="xs"
            variant="default"
            onClick={() =>
              setState({
                studioFilter: initFieldState(scopes.studioFilter),
                formatPartnerFilter: initFieldState(scopes.formatPartnerFilter),
                countryFilter: initFieldState(scopes.countryFilter),
                exhibitorAssociations: initFieldState(scopes.exhibitorAssociations),
              })
            }
            disabled={saving}
          >
            Reset
          </Button>
          <Button size="xs" onClick={handleSave} loading={saving} disabled={disabled}>
            Save Scopes
          </Button>
        </Group>
      )}
    </Stack>
  )
}
